import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  signal,
  output
} from '@angular/core';
import { DateTime } from 'luxon';
import { OrderType } from '../../../core/models/order-type.enum';
import { DeferService } from '../../../core/services/ordering/defer.service';
import { DropdownSelectionComponent } from '../../dropdown-selection/dropdown-selection.component';
import { IconNameType } from '../../icons/icon-name-type.enum';

@Component({
  selector: 'cwo-time-select',
  standalone: true,
  imports: [CommonModule, DropdownSelectionComponent],
  templateUrl: './time-select.component.html',
  styleUrl: './time-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSelectComponent implements OnInit, OnChanges {
  deferService = inject(DeferService);
  orderType = input<OrderType>(OrderType.Any);
  stared = input<boolean>(true);
  disabled = input<boolean>(false);
  inputDate = input<DateTime | undefined>();
  timeSlots = input<string[]>([]);
  iconNameType = IconNameType;

  readonly defferDateChanged = output<DateTime>();
  readonly availableHours = output<string[]>();

  formattedTimeSlots = signal<{ value: string; label: string }[]>([]);
  selectedTimeSlot = signal<string>('');

  ngOnInit(): void {
    if (this.inputDate() || this.timeSlots().length === 0) {
      this.fetchInitialTimeSlots();
    } else {
      this.updateTimeSlots();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    //we change that field and from other functions and components and dont want to trigger again the updateTimeSlots if we set the same value.
    //first time changes by fetchInitialTimeSlots we check if "firstChange". also if the previousValue is undefined or [] and lastly current is same as previous
    if ( changes['timeSlots']?.firstChange && changes['timeSlots']?.previousValue === undefined || changes['timeSlots']?.previousValue?.length === 0)return;
    
    if (changes['timeSlots']?.previousValue?.length !== changes['timeSlots']?.currentValue?.length) {
      this.updateTimeSlots(changes['timeSlots'].currentValue);
    }
  }

  private fetchInitialTimeSlots() {
    const date =
      this.inputDate() ?? DateTime.now().set({ second: 0, millisecond: 0 });
    this.deferService
      .getTimeSlots(date.toISO()!, this.orderType())
      .subscribe((timeSlots) => {
        this.availableHours.emit(timeSlots);
        this.updateTimeSlots(timeSlots);
      });
  }

  updateTimeSlots(slots = this.timeSlots()) {
    if (slots.length > 0) {
      const formattedSlots = slots.map((time) => ({
        value: time,
        label: this.formatTime(DateTime.fromISO(time)),
      }));
      this.formattedTimeSlots.set(formattedSlots);

      const selectedSlot = (this.inputDate())
        ? this.findDateFromInput(slots)
        : slots[0];

      if (selectedSlot === this.selectedTimeSlot()) return;

      const selectedTime = DateTime.fromISO(selectedSlot);
      this.selectedTimeSlot.set(selectedSlot);
      this.defferDateChanged.emit(selectedTime);

      return;
    }
  }

  onTimeSelectionChange(selectedIso: string) {
    const selectedTime = DateTime.fromISO(selectedIso);
    this.defferDateChanged.emit(selectedTime);
  }

  private findDateFromInput(timeSlots: string[]) {
    const matchingOrClosest =
      timeSlots.find((slot) =>
        DateTime.fromISO(slot).hasSame(this.inputDate()!, 'minute')
      ) ||
      timeSlots.reduce((closest, current) => {
        const currentDiff = Math.abs(
          DateTime.fromISO(current).diff(this.inputDate()!, 'minutes').minutes
        );
        const closestDiff = Math.abs(
          DateTime.fromISO(closest).diff(this.inputDate()!, 'minutes').minutes
        );
        return currentDiff < closestDiff ? current : closest;
      });
    return matchingOrClosest;
  }

  private formatTime(date: DateTime): string {
    return date.toFormat('hh:mm a');
  }
}
